"use client";

import React, { useEffect, useRef, useState } from "react";
import { FileText, User, Sparkles, X, CircleCheck } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const beforeItems = ["Gestion locative complexe et chronophage", "Tarification fixe et revenus sous-optimisés", "Photos amateurs peu attractives en ligne", "Maintenance et urgences à gérer soi-même", "Disponibilité limitée pour les voyageurs"];
const afterItems = ["Gestion intégrale pour une liberté totale", "Optimisation des prix par Dynamic Pricing", "Shooting photo professionnel haute définition", "Maintenance technique et urgences 24/7", "Accueil VIP et conciergerie haut de gamme", "assurance supplémentaire au caution habituel pour plus de sérénité"];
export default function ManageYourPracticeWithClarity() {
  const headerReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const cardsReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="172425" id="pourquoi-choisir-d-location" className="bg-background text-foreground py-24 px-6 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div ref={headerReveal.ref} className={`max-w-3xl mx-auto text-center transition-all duration-1000 ease-out ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight font-serif mb-6">
            Pourquoi Choisir D Location ?
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default">
            Découvrez comment notre expertise en conciergerie transforme la gestion de votre bien à Nice en une expérience sereine et hautement rentable.
          </p>
        </div>

        <div ref={cardsReveal.ref} className={`grid grid-cols-1 lg:grid-cols-2 gap-8 mt-16 transition-all duration-1000 delay-200 ease-out ${cardsReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <Card className="bg-muted/10 border-border/50 shadow-none flex flex-col h-full relative overflow-hidden group rounded-sm">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-background/5 pointer-events-none" />
            <CardHeader className="pb-8 pt-10 px-8">
              <div className="flex items-center gap-4 mb-2">
                <div className="p-3 bg-muted rounded-sm text-muted-foreground group-hover:scale-110 transition-transform duration-500">
                  <FileText className="w-6 h-6" />
                </div>
                <CardTitle className="text-2xl font-default text-muted-foreground uppercase tracking-wider">
                  GESTION SEUL <span className="text-foreground/50 font-medium text-lg ml-2 block sm:inline">Sans expertise</span>
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="px-8 pb-10 flex-1 flex flex-col">
              <div className="flex flex-col flex-1">
                {beforeItems.map((item, i) => <div key={i} data-index={i} className="flex flex-col">
                    <div className="flex items-start gap-4 py-5">
                      <div className="mt-0.5 bg-background/50 rounded-full p-1 border border-border/50 shrink-0">
                        <X className="w-4 h-4 text-destructive" />
                      </div>
                      <span className="text-muted-foreground text-lg leading-snug font-default">{item}</span>
                    </div>
                    {i < beforeItems.length - 1 && <Separator className="bg-border/40" />}
                  </div>)}
              </div>
            </CardContent>
          </Card>

          {}
          <Card className="bg-accent/5 border-accent/20 shadow-2xl shadow-accent/5 flex flex-col h-full relative overflow-hidden group rounded-sm">
            <div className="absolute inset-0 bg-gradient-to-br from-accent/5 via-transparent to-transparent pointer-events-none" />
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-accent/60 to-accent/20" />
            
            <CardHeader className="pb-8 pt-10 px-8 flex flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-accent/10 text-accent rounded-sm group-hover:scale-110 group-hover:bg-accent/20 transition-all duration-500">
                  <User className="w-6 h-6" />
                </div>
                <CardTitle className="text-2xl font-default text-foreground uppercase tracking-wider">
                  AVEC D LOCATION <span className="text-accent font-medium text-lg ml-2 block sm:inline">Conciergerie Premium</span>
                </CardTitle>
              </div>
              <Badge variant="secondary" className="hidden sm:inline-flex bg-accent/10 text-accent border-accent/20 shadow-sm py-1.5 px-3 rounded-sm">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                Performance & Sérénité
              </Badge>
            </CardHeader>
            <CardContent className="px-8 pb-10 flex-1 flex flex-col relative z-10">
              <div className="flex flex-col flex-1">
                {afterItems.map((item, i) => <div key={i} data-index={i} className="flex flex-col">
                    <div className="flex items-start gap-4 py-5">
                      <div className="mt-0.5 bg-emerald-500/10 rounded-full p-1 shrink-0">
                        <CircleCheck className="w-5 h-5 text-emerald-600" />
                      </div>
                      <span className="text-foreground text-lg leading-snug font-medium font-default">{item}</span>
                    </div>
                    {i < afterItems.length - 1 && <Separator className="bg-accent/10" />}
                  </div>)}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}